import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { css } from "@emotion/react";
import ArrowBack from "@mui/icons-material/ArrowBack";
import ArrowForward from "@mui/icons-material/ArrowForward";
import Circle from "@mui/icons-material/Circle";
import {
  Avatar,
  Box,
  Pagination,
  PaginationItem,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { styled, type Theme } from "@mui/material/styles";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { match, P } from "ts-pattern";

import { dayjs } from "@thalamos/common";
import type {
  ExtendedTeamMembership,
  ExtendedThalamosNotification,
} from "@thalamos/aspire-common";
import { getBaseFormTemplate } from "@thalamos/aspire-common";

import { api } from "../api.js";
import { LoggedInUserContext, NotificationsContext } from "../Contexts.js";
import { routeFns } from "../routePaths.js";
import type { DefaultPageProps } from "./defaultProps.js";
import {
  Banner,
  BannerList,
  Button,
  Dropdown,
  FormTitle,
  PopupDialog,
  PopupDialogTitle,
} from "~/components/design-system/index.js";
import { HorizontalLine } from "~/components/form/index.js";
import { stringAvatar } from "~/components/layout/menu/helpers/index.js";

interface NotificationsContainerProps {
  addMarginBottom?: boolean;
  wasViewed?: boolean;
  theme?: Theme;
}

export const NotificationsContainer = styled("div", {
  shouldForwardProp: (prop) =>
    prop !== "addMarginBottom" && prop !== "wasViewed",
})<NotificationsContainerProps>(({ theme, addMarginBottom, wasViewed }) => ({
  padding: theme.spacing(3),
  width: "100%",
  ...(wasViewed && { backgroundColor: theme.palette.secondary.light }),
  border: `0.5px solid ${theme.palette.background.default}`,
  borderRadius: "6px",
  transition: "box-shadow 0.3s ease-in-out",
  "&:hover": {
    cursor: "pointer",
    boxShadow: "0px 4px 4px rgba(84, 89, 107, 0.25)",
  },
  ...(addMarginBottom && { marginBottom: theme.spacing(4) }),
}));

function TeamSelectionDialog({
  onClose,
  onConfirm,
  teams,
  currentTeamId,
}: {
  onClose: () => void;
  onConfirm: (teamId: string) => void;
  teams: ExtendedTeamMembership[];
  currentTeamId: string;
}) {
  const { t } = useTranslation();

  const teamValues = teams.map((m) => ({
    value: m.teamId,
    label: m.teamName,
  }));

  const initialTeamId = teamValues.some((tv) => tv.value === currentTeamId)
    ? currentTeamId
    : (teamValues[0]?.value ?? currentTeamId);

  const [selectedTeamId, setSelectedTeamId] = useState(initialTeamId);

  return (
    <PopupDialog open onClose={onClose}>
      <PopupDialogTitle
        titleText={t("pages.notifications.teamSelectionDialog.title")}
        closeDialog={onClose}
      />

      <Banner
        bannerType={BannerList.INFO}
        title={t("pages.notifications.teamSelectionDialog.bannerInfo")}
      />

      <Box sx={{ mt: 3 }}>
        <Dropdown
          name="team-selector"
          label={t("pages.notifications.teamSelectionDialog.teamLabel")}
          values={teamValues}
          selectedValue={selectedTeamId}
          onChange={(value: string) => {
            setSelectedTeamId(value);
          }}
        />
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 4 }}>
        <Button
          variant="outlined"
          label={t("pages.notifications.teamSelectionDialog.close")}
          onClick={onClose}
        />
        <Button
          variant="contained"
          label={t("pages.notifications.teamSelectionDialog.continue")}
          onClick={() => {
            onConfirm(selectedTeamId);
          }}
        />
      </Box>
    </PopupDialog>
  );
}

function notificationFormLabel({
  notification,
  formName,
  t,
}: {
  notification: ExtendedThalamosNotification;
  formName: string | undefined;
  t: TFunction;
}): string {
  if (notification.visionIncidentId) {
    return (
      match(notification.visionIncidentType)
        .with("mca", () => t("pages.notifications.mentalCapacityAct"))
        .with("s136", P.nullish, () => t("pages.notifications.section136"))
        // A page loaded before a new kind of incident was released still
        // renders the notification, with a neutral label.
        .exhaustive(() => t("pages.notifications.policeIncident"))
    );
  }
  return match(notification.formContextType)
    .with("soad-assessment", () => t("pages.notifications.soadAssessment"))
    .with("police-incident", () => t("pages.notifications.section136"))
    .with("admission", () => t("pages.notifications.mhaAssessment"))
    .with("mca-assessment", "standalone", P.nullish, () => formName ?? "")
    .exhaustive();
}

function useVisionNotificationHandler() {
  const navigate = useNavigate();
  const userContext = useContext(LoggedInUserContext);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [pendingIncidentId, setPendingIncidentId] = useState<string | null>(
    null,
  );

  const currentTeamId = userContext?.user.sessionContext?.teamId;

  const nonAdminTeamMemberships = useMemo(
    () =>
      (userContext?.user.memberships ?? []).filter(
        (m): m is ExtendedTeamMembership =>
          m.type === "team" &&
          m.teamStatus === "active" &&
          m.teamType !== "mha" &&
          m.role === "member",
      ),
    [userContext?.user.memberships],
  );

  const handleClick = useCallback(
    (visionIncidentId: string) => {
      if (nonAdminTeamMemberships.length <= 1) {
        navigate(routeFns.visionPreviewIncident(visionIncidentId));
        return;
      }

      setPendingIncidentId(visionIncidentId);
      setDialogOpen(true);
    },
    [nonAdminTeamMemberships.length, navigate],
  );

  const handleTeamSelected = useCallback(
    async (teamId: string) => {
      if (!pendingIncidentId) return;

      try {
        if (teamId !== currentTeamId) {
          const membership = nonAdminTeamMemberships.find(
            (m) => m.teamId === teamId,
          );
          if (membership) {
            await api.users.setSessionContext({
              type: "team",
              teamId: membership.teamId,
              role: membership.role,
            });
            userContext?.refetchUser();
          }
        }

        setDialogOpen(false);
        navigate(routeFns.visionPreviewIncident(pendingIncidentId));
      } catch {
        setDialogOpen(false);
      }
    },
    [
      pendingIncidentId,
      currentTeamId,
      nonAdminTeamMemberships,
      userContext,
      navigate,
    ],
  );

  const renderDialog = useCallback(() => {
    if (!dialogOpen || !currentTeamId) return null;

    return (
      <TeamSelectionDialog
        onClose={() => {
          setDialogOpen(false);
        }}
        onConfirm={(teamId) => {
          void handleTeamSelected(teamId);
        }}
        teams={nonAdminTeamMemberships}
        currentTeamId={currentTeamId}
      />
    );
  }, [dialogOpen, currentTeamId, handleTeamSelected, nonAdminTeamMemberships]);

  return { handleClick, renderDialog };
}

export function NotificationsPageInner({
  notifications,
  notificationsPage,
  setNotificationsPage,
  numberNotifications,
  markAsViewed,
}: {
  numberNotifications: number;
  notifications: ExtendedThalamosNotification[];
  notificationsPage: number;
  setNotificationsPage: (page: number) => void;
  markAsViewed: (notificationIds: string[]) => void;
}) {
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useTranslation();

  const visionHandler = useVisionNotificationHandler();

  useEffect(() => {
    setNotificationsPage(0);
  }, []);

  function markAllAsViewed() {
    markAsViewed(notifications.filter((n) => !n.viewed).map((n) => n.id));
  }

  const wasViewed = useMemo(
    () => notifications.filter((n) => !n.viewed).map((n) => n.id),
    [notifications],
  );

  useEffect(markAllAsViewed, [notifications]);

  const numPages = Math.floor(numberNotifications / 100) + 1;

  return notifications.length > 0 ? (
    <Box>
      <FormTitle
        titleText={t("pages.notifications.title")}
        hasTitleBottomMargin={false}
        useReducedTopPadding
      />
      {notifications.map((notification) => {
        const form = (notification.forms ?? []).find(
          (f) => f.id === notification.workItem?.formId,
        );
        const template =
          form &&
          getBaseFormTemplate(form.formTemplate.id, form.formTemplate.version);

        const hasBeenViewed = wasViewed.includes(notification.id);

        return (
          <Box
            key={notification.id}
            sx={{ mb: 3 }}
            onClick={() => {
              if (notification.interviewShareCode) {
                navigate(
                  routeFns.interviewInviteResponsePage(
                    notification.interviewShareCode,
                  ),
                );
              } else if (
                notification.formContextId &&
                notification.patient.id
              ) {
                navigate(
                  routeFns.formContextPage(
                    notification.formContextId,
                    notification.patient.id,
                  ),
                );
              } else if (notification.visionIncidentId) {
                visionHandler.handleClick(notification.visionIncidentId);
              }
            }}
          >
            <NotificationsContainer wasViewed={hasBeenViewed}>
              <Stack direction="row">
                <Stack
                  direction="row"
                  spacing={2}
                  sx={{
                    alignItems: { sm: "flex-start", md: "center" },
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: theme.palette.primary.main,
                      width: 30,
                      height: 30,
                      fontWeight: 700,
                      fontSize: 15,
                    }}
                    {...stringAvatar(notification.createdByTeamName)}
                  />
                  <Typography>
                    {notification.text} (
                    {notificationFormLabel({
                      notification,
                      formName: template?.formName,
                      t,
                    })}
                    )
                  </Typography>

                  <Typography
                    sx={{
                      color: "text.lightGray",
                      fontWeight: "400",
                    }}
                  >
                    {dayjs.utc(notification.createdAt).fromNow()}
                  </Typography>
                </Stack>
                {hasBeenViewed && (
                  <Box
                    css={css`
                      margin-left: auto;
                    `}
                  >
                    <Circle
                      style={{ fontSize: 12 }}
                      sx={{ color: "primary.main" }}
                    />
                  </Box>
                )}
              </Stack>
              <Box sx={{ display: "flex", flexDirection: "column", ml: 5.8 }}>
                <Box sx={{ my: 1.2 }}>
                  <HorizontalLine noMargin />
                </Box>
                <Typography
                  sx={{
                    fontSize: 13,
                    color: "text.lightGray",
                    fontWeight: 400,
                  }}
                >
                  {t("pages.notifications.patientLabel")}
                </Typography>
                <Typography>
                  {!notification.visionIncidentId &&
                    notification.patient.name &&
                    `${notification.patient.name.family}, ${notification.patient.name.given}`}
                </Typography>
              </Box>
            </NotificationsContainer>
          </Box>
        );
      })}
      <Box
        sx={{
          display: "flex",
          width: "100%",
          justifyContent: "center",
          mb: 1,
        }}
      >
        <Pagination
          sx={{
            ul: {
              "& .MuiPaginationItem-root.MuiPaginationItem-root.Mui-selected": {
                background: theme.palette.primary.main,
                color: theme.palette.common.white,
              },
            },
          }}
          count={numPages}
          page={notificationsPage + 1}
          onChange={(_event: unknown, value: number) => {
            setNotificationsPage(value - 1);
          }}
          renderItem={(item) => (
            <PaginationItem
              {...item}
              slots={{
                previous: ArrowBack,
                next: ArrowForward,
              }}
            />
          )}
        />
      </Box>

      {visionHandler.renderDialog()}
    </Box>
  ) : (
    <>{t("pages.notifications.noNotifications")}</>
  );
}

export function NotificationsPage(_props: DefaultPageProps) {
  const { notifications, notificationsPage, setNotificationsPage } =
    useContext(NotificationsContext);

  return notifications ? (
    <NotificationsPageInner
      notifications={notifications.notifications}
      numberNotifications={notifications.count}
      notificationsPage={notificationsPage}
      setNotificationsPage={setNotificationsPage}
      markAsViewed={(ids) => {
        void api.notifications.markAsViewed(ids);
      }}
    />
  ) : (
    <></>
  );
}
